import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { detectBoundary, getBoundaryPositions, computeBoundaryCentroid } from './boundaryDetection';

/**
 * Order boundary vertices by proximity to form a continuous curve
 * (Matches the algorithm in boundaryDetection.js)
 */
function orderBoundaryVerticesByProximity(boundaryIndices, boundaryPositions) {
  if (boundaryIndices.length === 0) return [];

  const ordered = [boundaryIndices[0]];
  const remaining = new Set(boundaryIndices.slice(1));

  while (remaining.size > 0) {
    const lastIdx = ordered[ordered.length - 1];
    const lastIdxInArray = boundaryIndices.indexOf(lastIdx);
    const lastPos = boundaryPositions[lastIdxInArray];

    let nearestIdx = null;
    let minDist = Infinity;

    for (const idx of remaining) {
      const idxInArray = boundaryIndices.indexOf(idx);
      const pos = boundaryPositions[idxInArray];
      const dist = lastPos.distanceTo(pos);

      if (dist < minDist) {
        minDist = dist;
        nearestIdx = idx;
      }
    }

    if (nearestIdx !== null) {
      ordered.push(nearestIdx);
      remaining.delete(nearestIdx);
    } else {
      break;
    }
  }

  return ordered;
}

/**
 * Compute smoothed boundary curve normals (deflection directions)
 * Simplified version of computeBoundaryCurveNormals from boundaryDetection.js
 * Returns Map of boundaryIndex -> deflection direction (in world space, XZ plane)
 */
function computeBoundaryCurveNormals(orderedIndices, boundaryIndices, boundaryPositions, mesh, centroid) {
  const deflectionMap = new Map();
  const geometry = mesh.geometry;
  const normals = geometry.attributes.normal;

  if (!normals) {
    geometry.computeVertexNormals();
  }

  const n = orderedIndices.length;

  if (n < 3) {
    // Fallback: use radial from centroid
    for (let i = 0; i < boundaryIndices.length; i++) {
      const pos = boundaryPositions[i];
      const radial = new THREE.Vector3(
        pos.x - centroid.x,
        0,
        pos.z - centroid.z
      ).normalize();
      deflectionMap.set(boundaryIndices[i], radial);
    }
    return deflectionMap;
  }

  // Compute initial directions from surface normals
  const initialDirections = new Map();

  for (const boundaryIdx of orderedIndices) {
    // Get vertex normal in world space
    const normal = new THREE.Vector3();
    normal.fromBufferAttribute(normals, boundaryIdx);
    normal.applyMatrix4(mesh.matrixWorld).normalize();

    // Project to XZ plane
    const projected = new THREE.Vector3(normal.x, 0, normal.z);

    const boundaryIdxInArray = boundaryIndices.indexOf(boundaryIdx);
    const pos = boundaryPositions[boundaryIdxInArray];

    // If normal is too vertical, use radial from centroid
    if (projected.length() < 0.1) {
      projected.set(
        pos.x - centroid.x,
        0,
        pos.z - centroid.z
      );
    }

    projected.normalize();
    initialDirections.set(boundaryIdx, projected);
  }

  // Apply smoothing passes
  const SMOOTH_PASSES = 2;

  for (let pass = 0; pass < SMOOTH_PASSES; pass++) {
    const smoothed = new Map();

    for (let i = 0; i < n; i++) {
      const currentIdx = orderedIndices[i];
      const prevIdx = orderedIndices[(i - 1 + n) % n];
      const nextIdx = orderedIndices[(i + 1) % n];

      const current = initialDirections.get(currentIdx);
      const prev = initialDirections.get(prevIdx);
      const next = initialDirections.get(nextIdx);

      const NEIGHBOR_WEIGHT = 0.15;

      // Weighted average
      const avg = new THREE.Vector3()
        .add(current.clone().multiplyScalar(1 - 2 * NEIGHBOR_WEIGHT))
        .add(prev.clone().multiplyScalar(NEIGHBOR_WEIGHT))
        .add(next.clone().multiplyScalar(NEIGHBOR_WEIGHT));

      avg.normalize();
      smoothed.set(currentIdx, avg);
    }

    // Update for next pass
    for (const [idx, dir] of smoothed) {
      initialDirections.set(idx, dir);
    }
  }

  // Final check - ensure all directions point outward
  for (const [boundaryIdx, direction] of initialDirections) {
    const boundaryIdxInArray = boundaryIndices.indexOf(boundaryIdx);
    const pos = boundaryPositions[boundaryIdxInArray];

    const radial = new THREE.Vector3(
      pos.x - centroid.x,
      0,
      pos.z - centroid.z
    ).normalize();

    // If direction points inward, use radial
    if (direction.dot(radial) < 0) {
      direction.set(radial.x, radial.y, radial.z).normalize();
    }

    deflectionMap.set(boundaryIdx, direction);
  }

  return deflectionMap;
}

/**
 * Map ordered boundary vertices to segments
 * Returns array of {segmentIndex, orderedVertices[]} preserving order within each segment
 */
function mapOrderedVerticesToSegments(orderedIndices, boundaryIndices, boundaryPositions, centroid, flareValues) {
  // Map each vertex to its segment
  const vertexToSegment = new Map();

  for (const vertexIdx of orderedIndices) {
    const idxInArray = boundaryIndices.indexOf(vertexIdx);
    const pos = boundaryPositions[idxInArray];

    // Calculate angle (0° at top/north, clockwise)
    const dx = pos.x - centroid.x;
    const dz = pos.z - centroid.z;
    let angle = Math.atan2(dx, -dz) * (180 / Math.PI);
    if (angle < 0) angle += 360;

    const anglePercent = (angle / 360) * 100;

    // Find segment
    let segmentIndex = -1;
    for (let i = 0; i < flareValues.length; i++) {
      const startPercent = i === 0 ? flareValues[flareValues.length - 1] : flareValues[i - 1];
      const endPercent = flareValues[i];

      if (endPercent < startPercent) {
        // Wrap-around segment
        if (anglePercent >= startPercent || anglePercent < endPercent) {
          segmentIndex = i;
          break;
        }
      } else {
        // Normal segment
        if (anglePercent >= startPercent && anglePercent < endPercent) {
          segmentIndex = i;
          break;
        }
      }
    }

    if (segmentIndex !== -1) {
      vertexToSegment.set(vertexIdx, segmentIndex);
    }
  }

  // Group vertices by segment while preserving order
  const segmentGroups = [];
  for (let s = 0; s < flareValues.length; s++) {
    segmentGroups.push({ segmentIndex: s, orderedVertices: [] });
  }

  for (const vertexIdx of orderedIndices) {
    const segmentIdx = vertexToSegment.get(vertexIdx);
    if (segmentIdx !== undefined) {
      segmentGroups[segmentIdx].orderedVertices.push(vertexIdx);
    }
  }

  return segmentGroups;
}

/**
 * Main debug visualization function
 * Creates Line2 for each segment showing deflected boundary vertices
 */
export function createFlareDebugVisualization(shadedMesh, trimlineState, flareState, scene) {
  const debugLines = [];

  if (!shadedMesh || !scene) return debugLines;

  // Import the actual boundary detection function
  // Step 1: Detect boundary vertices
  const boundaryVertices = detectBoundary(shadedMesh, trimlineState);
  if (boundaryVertices.length === 0) {
    return debugLines;
  }

  // Step 2: Get boundary positions in world space
  const boundaryPositions = getBoundaryPositions(shadedMesh, boundaryVertices);

  // Step 3: Compute centroid
  const centroid = computeBoundaryCentroid(boundaryPositions);

  // Step 4: Order vertices by proximity to form continuous curve
  const orderedIndices = orderBoundaryVerticesByProximity(boundaryVertices, boundaryPositions);

  // Step 5: Compute boundary curve normals (deflection directions)
  const curveNormals = computeBoundaryCurveNormals(
    orderedIndices,
    boundaryVertices,
    boundaryPositions,
    shadedMesh,
    centroid
  );

  // Step 6: Map ordered vertices to segments
  const segmentGroups = mapOrderedVerticesToSegments(
    orderedIndices,
    boundaryVertices,
    boundaryPositions,
    centroid,
    flareState.flareValues
  );

  // Step 7: For each segment, create Line2 with deflected positions
  const SEGMENT_COLORS = {
    purple: 0xB984D9,
    yellow: 0xFFD152,
    orange: 0xF19B4A,
    blue: 0x3B9FF5,
    violet: 0x9093DF
  };

  // Store deflected positions for each segment (for connecting later)
  const segmentDeflectedPositions = new Map();

  for (const { segmentIndex, orderedVertices } of segmentGroups) {
    if (orderedVertices.length < 2) continue;

    const deflectValue = flareState.segmentDeflectValues[segmentIndex];
    const points = [];
    const deflectedPositions = [];

    // Apply deflection to each ordered vertex
    for (const vertexIdx of orderedVertices) {
      const idxInArray = boundaryVertices.indexOf(vertexIdx);
      const originalPos = boundaryPositions[idxInArray];
      const deflectionDir = curveNormals.get(vertexIdx);

      if (!deflectionDir) continue;

      // Move position along deflection direction
      // deflectValue is in mm, convert to scene units (1mm = 0.1 units)
      const deflectUnits = deflectValue * 0.1;
      const deflectedPos = originalPos.clone();
      deflectedPos.x += deflectionDir.x * deflectUnits;
      deflectedPos.z += deflectionDir.z * deflectUnits;

      points.push(deflectedPos.x, deflectedPos.y, deflectedPos.z);
      deflectedPositions.push(deflectedPos);
    }

    if (points.length < 6) continue; // Need at least 2 points

    // Store deflected positions for this segment
    segmentDeflectedPositions.set(segmentIndex, deflectedPositions);

    // Create Line2 for this segment
    const lineGeometry = new LineGeometry();
    lineGeometry.setPositions(points);

    const segmentName = flareState.segmentNames[segmentIndex];
    const color = SEGMENT_COLORS[segmentName] || 0x808080;

    const lineMaterial = new LineMaterial({
      color: color,
      linewidth: 4, // pixels
      resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
      dashed: false,
      depthTest: true,
      depthWrite: true,
      transparent: false
    });

    const line = new Line2(lineGeometry, lineMaterial);
    line.name = `FlareDebug_Segment_${segmentIndex}_${segmentName}`;
    line.computeLineDistances();

    scene.add(line);
    debugLines.push(line);
  }

  // Step 8: Create connection lines between segments to form closed loop
  const CONNECTION_COLOR = 0xFFFFFF; // White for connection lines
  const segmentCount = flareState.segmentNames.length;

  for (let i = 0; i < segmentCount; i++) {
    const currentSegmentPositions = segmentDeflectedPositions.get(i);
    const nextSegmentIndex = (i + 1) % segmentCount;
    const nextSegmentPositions = segmentDeflectedPositions.get(nextSegmentIndex);

    if (!currentSegmentPositions || currentSegmentPositions.length === 0) continue;
    if (!nextSegmentPositions || nextSegmentPositions.length === 0) continue;

    // Connect the last position of current segment to first position of next segment
    const lastPos = currentSegmentPositions[currentSegmentPositions.length - 1];
    const firstPos = nextSegmentPositions[0];

    const connectionPoints = [
      lastPos.x, lastPos.y, lastPos.z,
      firstPos.x, firstPos.y, firstPos.z
    ];

    const connectionGeometry = new LineGeometry();
    connectionGeometry.setPositions(connectionPoints);

    const connectionMaterial = new LineMaterial({
      color: CONNECTION_COLOR,
      linewidth: 3, // Slightly thinner than segment lines
      resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
      dashed: false,
      depthTest: true,
      depthWrite: true,
      transparent: false
    });

    const connectionLine = new Line2(connectionGeometry, connectionMaterial);
    connectionLine.name = `FlareDebug_Connection_${i}_to_${nextSegmentIndex}`;
    connectionLine.computeLineDistances();

    scene.add(connectionLine);
    debugLines.push(connectionLine);
  }

  return debugLines;
}

/**
 * Dispose debug visualization lines
 */
export function disposeDebugVisualization(debugLines, scene) {
  for (const line of debugLines) {
    if (line.parent) {
      line.parent.remove(line);
    }
    scene.remove(line);
    line.geometry?.dispose?.();
    line.material?.dispose?.();
  }
  debugLines.length = 0;
}
